// 前端 → 轻量服务端（/api 经 setupProxy 转发到 localhost:4000）
// 登录态、账号管理、配额、导演台历史、LLM 提问都走这里

export interface AuthUser {
  username: string;
  role: 'admin' | 'user';
  usedCount: number;
  remainingQuota: number;
  questionLimit: number;
  createdAt: string;
}

export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

const TOKEN_KEY = 'kk_token';

export const getToken = (): string => localStorage.getItem(TOKEN_KEY) || '';
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    // 登录过期：清token刷新回登录页
    clearToken();
    window.location.reload();
    throw new ApiError('未登录或登录已过期');
  }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError((body && body.error) || `HTTP ${res.status}`, body && body.code);
  }
  return body as T;
};

// ---------- 认证 ----------
export const apiLogin = (username: string, password: string) =>
  request<{ token: string; user: AuthUser; llmReady: boolean }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });

export const apiLogout = () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' });

export const apiMe = () =>
  request<{ user: AuthUser; llmReady: boolean }>('/auth/me');

// ---------- 账号管理（管理员） ----------
export const apiListUsers = () => request<{ users: AuthUser[] }>('/users');

export const apiCreateUser = (username: string, password: string, role: 'admin' | 'user', questionLimit?: number) =>
  request<{ user: AuthUser }>('/users', {
    method: 'POST',
    body: JSON.stringify({ username, password, role, questionLimit }),
  });

export const apiAdjustQuota = (username: string, questionLimit: number) =>
  request<{ user: AuthUser }>(`/users/${encodeURIComponent(username)}/adjust-quota`, {
    method: 'POST',
    body: JSON.stringify({ questionLimit }),
  });

export const apiResetQuota = (username: string) =>
  request<{ user: AuthUser }>(`/users/${encodeURIComponent(username)}/reset-quota`, { method: 'POST' });

export const apiResetPassword = (username: string, password: string) =>
  request<{ ok: boolean }>(`/users/${encodeURIComponent(username)}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });

export const apiDeleteUser = (username: string) =>
  request<{ ok: boolean }>(`/users/${encodeURIComponent(username)}`, { method: 'DELETE' });

// ---------- 导演台会话/历史/提问 ----------
export interface SessionSummary {
  id: number;
  projectId: string;
  projectName: string;
  role: string;
  title: string;
  createdAt: string;
  messageCount: number;
}

export interface SessionMessage {
  role: 'user' | 'assistant';
  content: string;
  time: string;
}

export const apiListSessions = () => request<{ sessions: SessionSummary[] }>('/sessions');

export const apiEnsureSession = (projectId: string, projectName: string, role: string) =>
  request<{ sessionId: number }>('/sessions', {
    method: 'POST',
    body: JSON.stringify({ projectId, projectName, role }),
  });

export const apiSessionMessages = (sessionId: number) =>
  request<{ session: { id: number; projectName: string; role: string; title: string }; messages: SessionMessage[] }>(
    `/sessions/${sessionId}/messages`
  );

export const apiAppendAssistant = (sessionId: number, content: string) =>
  request<{ ok: boolean }>(`/sessions/${sessionId}/assistant`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });

// 会话消息追加（成功记 user+assistant；失败先只记 user，重试成功再补 assistant）
export const apiAppendMessage = (sessionId: number, role: 'user' | 'assistant', content: string) =>
  request<{ ok: boolean }>(`/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ role, content }),
  });

// ---------- 演示投放数据（Performance Snapshot） ----------
export interface PerformanceSnapshot {
  id: number;
  mode: 'DEMO';
  snapshot_name: string;
  snapshot_time: string;
  is_active: boolean;
}

export const apiListPerformanceSnapshots = (projectId: string) =>
  request<{ snapshots: PerformanceSnapshot[]; active_id: number | null; mode: 'DEMO' }>(
    `/performance/snapshots?project_id=${encodeURIComponent(projectId)}`
  );

export const apiActivatePerformanceSnapshot = (projectId: string, snapshotId: number) =>
  request<{ ok: boolean; snapshots: PerformanceSnapshot[]; active_id: number }>(
    '/performance/snapshots/activate',
    { method: 'POST', body: JSON.stringify({ project_id: projectId, snapshot_id: snapshotId }) }
  );

// ---------- AI接口管理（仅管理员） ----------
export interface ManagedProvider {
  id: string;
  name: string;
  provider_type: string;
  base_url: string;
  model: string;
  priority: number;
  enabled: boolean;
  quota_cooldown_seconds: number;
  key_last4: string;
  status: string;
  cooldown_until_iso: string | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface PoolStatusItem {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  status: string;
  cooldown_until: string | null;
  last_success_at: string | null;
  last_error_type: string | null;
  model: string;
  quota_cooldown_seconds: number;
}

export interface ProviderLogEntry {
  time: string;
  task: string;
  provider_id: string;
  provider_name: string;
  model: string;
  status: string;
  latency: number;
  fallback: 'yes' | 'no';
  error_type: string | null;
}

export const apiListProviders = () =>
  request<{ providers: ManagedProvider[]; mode: 'managed' | 'env'; master_key_configured: boolean; pool: PoolStatusItem[] }>(
    '/admin/providers'
  );

export const apiCreateProvider = (input: {
  name: string; provider_type?: string; base_url: string; model: string;
  api_key: string; enabled?: boolean; quota_cooldown_seconds?: number;
}) =>
  request<{ provider: ManagedProvider }>('/admin/providers', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const apiUpdateProvider = (id: string, input: {
  name?: string; provider_type?: string; base_url?: string; model?: string;
  api_key?: string; enabled?: boolean; quota_cooldown_seconds?: number;
}) =>
  request<{ provider: ManagedProvider }>(`/admin/providers/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const apiProviderAction = (id: string, action: 'enable' | 'disable' | 'up' | 'down' | 'test') =>
  request<{ provider?: ManagedProvider; providers?: ManagedProvider[]; ok?: boolean; status?: string; message?: string; latency?: number }>(
    `/admin/providers/${encodeURIComponent(id)}/${action}`,
    { method: 'POST' }
  );

export const apiDeleteProvider = (id: string) =>
  request<{ ok: boolean; providers: ManagedProvider[] }>(`/admin/providers/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const apiImportEnvProviders = () =>
  request<{ ok: boolean; imported: number; providers: ManagedProvider[] }>('/admin/providers/import-env', { method: 'POST' });

export const apiProviderLogs = () =>
  request<{ logs: ProviderLogEntry[] }>('/admin/providers/logs');
